import React from "react";
import HomeSideNavBar from "./HomeSideNavBar";
import Header from "./Header";
import ApiDataProvider from "./ApiDataContext";
import {Boxes, MapPin, ShieldCheck, Truck} from "lucide-react";
import packageJson from "../../package.json";
import PageHeader from "../components/layout/PageHeader";

const About = () => {
  return (
    <ApiDataProvider>
      <div className="container">
        <div className="screen-layout">
          <Header />
          <HomeSideNavBar />
          <main className="flex-1 min-w-0 min-h-0 flex flex-col bg-surface-muted overflow-y-auto">
            <PageHeader
              title="About Yekkada"
              subtitle="Fleet tracking, locks, and operations — built for logistics teams that can't afford downtime."
              breadcrumbs={[{label: "About"}]}
            />
            <div className="max-w-[1024px] mx-auto px-6 lg:px-8 py-8 space-y-8 w-full">
              <section className="bg-white rounded-xl border border-ink-200 shadow-xs p-6">
                <h2 className="text-xl font-semibold text-ink-900">Yekkada v{packageJson.version}</h2>
                <p className="text-base text-ink-600 mt-2 leading-relaxed">
                  Yekkada is the end-to-end platform for live fleet visibility, lock
                  control, and audit-grade reporting. Operators manage trackers and
                  Bluetooth locks, allocate them to drivers, and respond to events in
                  real time.
                </p>
              </section>

              <section>
                <h3 className="text-base font-semibold text-ink-900 mb-3">What's in the box</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Capability icon={Truck} title="Live tracking" body="Real-time positions, motion, battery, and lock state for every device — refreshed in seconds via WebSocket." />
                  <Capability icon={ShieldCheck} title="Lock control" body="Issue lock / unlock commands instantly. One-time passcodes for Bluetooth locks. Mid-trip suspend prevented automatically." />
                  <Capability icon={Boxes} title="Asset + user management" body="Register devices, assign to drivers, suspend / reactivate, manage labels — all from a single screen with bulk actions." />
                  <Capability icon={MapPin} title="Reports + audit" body="Trip route playback, daily activity, fleet distance, alert log, frame log, plus a tenant audit trail of every action." />
                </div>
              </section>

              <section className="bg-white rounded-xl border border-ink-200 shadow-xs p-6">
                <h3 className="text-base font-semibold text-ink-900">Need help?</h3>
                <p className="text-sm text-ink-600 mt-2">
                  Open the command palette with <kbd className="font-mono text-xs border border-ink-200 rounded px-1.5 py-0.5">⌘K</kbd> /{" "}
                  <kbd className="font-mono text-xs border border-ink-200 rounded px-1.5 py-0.5">Ctrl K</kbd> from any page to find what you need fast.
                </p>
              </section>
            </div>
          </main>
        </div>
      </div>
    </ApiDataProvider>
  );
};

function Capability({icon: Icon, title, body}) {
  return (
    <div className="bg-white rounded-xl border border-ink-200 shadow-xs p-4 flex items-start gap-3">
      <div className="h-9 w-9 rounded-lg bg-brand-50 grid place-items-center shrink-0">
        <Icon className="h-4 w-4 text-brand-700" aria-hidden="true" />
      </div>
      <div>
        <p className="text-sm font-semibold text-ink-900">{title}</p>
        <p className="text-xs text-ink-500 mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

export default About;
